import sortBy from "lodash.sortby";
import React, { useMemo } from "react";

import { IgTextField, IgTypography } from "@intelligems/ig-theme";
import { OrganizationSummaryDto } from "@intelligems/ig-types";
import { Grid, Skeleton } from "@mui/material";
import { useGetOrganizationSummaries } from "@src/api/organization-summaries/get-organization-summaries";
import { VirtualAutoComplete } from "@src/components/VirtualAutoComplete/VirtualAutoComplete";
import { filterByNameOrId } from "@src/features/admin/components/Fields/filter-by-name-or-id";

const Formatter = (data: OrganizationSummaryDto) => {
  return (
    <Grid container columnSpacing={1} justifyContent={"space-between"}>
      <Grid item>
        <IgTypography>{data.name}</IgTypography>
      </Grid>
      {data.stripeSubscriptionSummaries.filter((ss) =>
        ["active", "trialing"].includes(ss.status)
      ).length > 0 || data.agencies.length > 0 ? (
        <Grid item>
          <IgTypography sx={{ fontStyle: "italic" }} variant={"body2"}>
            {data.agencies.length ? "In Agency" : "Has Subscription"}
          </IgTypography>
        </Grid>
      ) : null}
    </Grid>
  );
};

interface ChooseCustomerProps {
  blacklistedIds?: string[];
  whitelistedIds?: string[];
  org: OrganizationSummaryDto | null;
  onChange: (value: OrganizationSummaryDto) => void;
  disabled?: boolean;
}

export const ChooseAdminOrgField = ({
  blacklistedIds,
  whitelistedIds,
  org,
  onChange,
  disabled,
}: ChooseCustomerProps) => {
  const adminOrgs = useGetOrganizationSummaries();

  const data = useMemo(() => {
    if (!adminOrgs.isSuccess) {
      return [];
    }
    let named = adminOrgs.data.orgs.filter((c) => !!c.name);
    if (blacklistedIds) {
      named = named.filter((c) => !blacklistedIds.includes(c.id));
    } else if (whitelistedIds) {
      named = named.filter((c) => whitelistedIds.includes(c.id));
    }

    return sortBy(named, (_) => _.name || _.id);
  }, [adminOrgs.isSuccess, adminOrgs.data]);

  if (!adminOrgs.isSuccess) {
    return <Skeleton variant="rectangular" height={29} />;
  }

  return (
    <VirtualAutoComplete<OrganizationSummaryDto, false, false, false>
      formatter={Formatter}
      onChange={(e, newValue: OrganizationSummaryDto) => {
        onChange(newValue);
      }}
      componentsProps={{
        paper: {
          sx: {
            "&.MuiPaper-root": {
              width: "275px",
            },
          },
        },
      }}
      getOptionDisabled={(option) =>
        option.stripeSubscriptionSummaries.filter((ss) =>
          ["active", "trialing"].includes(ss.status)
        ).length > 0 || option.agencies.length > 0
      }
      disabled={disabled}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      options={data}
      getOptionLabel={(option: OrganizationSummaryDto) => option.name}
      renderInput={(params) => <IgTextField {...params} variant={"standard"} />}
      value={org}
      label={"Organization"}
      filterOptions={filterByNameOrId}
    />
  );
};
